// src/components/appointments/AppointmentDetails.tsx
import React, { useMemo, useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Calendar, Clock, User, Lock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { he } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import OtpDialog from '../../lib/OtpDialog';

interface AppointmentDetailsProps {
  appointment: any;
  onClose: () => void;
  onCancel: (id: string) => Promise<void>;
  isAutofillOffer?: boolean;
}

function normalizePhone(p: string) {
  const s = (p || '').trim();
  const clean = s.replace(/[^\d+]/g, '');
  if (clean.startsWith('+972')) return clean;
  if (clean.startsWith('05')) return '+972' + clean.slice(1);
  return clean;
}

/**
 * מחזיר את ה-booking link הנקי (ללא query params) מתוך ה-URL הנוכחי:
 * /book/<token>?autofill=1&appointmentId=...  =>  /book/<token>
 */
function getCleanBookingUrl(): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}`;
}

export function AppointmentDetails({
  appointment,
  onClose,
  onCancel,
  isAutofillOffer = false,
}: AppointmentDetailsProps) {
  const [loading, setLoading] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showAuthExplanation, setShowAuthExplanation] = useState(false);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showOtpDialog, setShowOtpDialog] = useState(false);

  const businessId = localStorage.getItem('businessId') || '';

  /**
   * מחזיר את ה-booking link הבסיסי ללא query params
   */
  const getBookingLinkWithoutQueryParams = () => {
    const url = new URL(window.location.href);
    return `${url.origin}${url.pathname}`;
  };

  const statusConfig = useMemo(() => {
    switch (appointment?.status) {
      case 'booked':
        return { text: 'ממתין לאישור', bg: '#FEF3C7', color: '#D97706' };
      case 'confirmed':
        return { text: 'מאושר', bg: '#D1FAE5', color: '#059669' };
      case 'completed':
      case 'done':
        return { text: 'הושלם', bg: '#E5E7EB', color: '#6B7280' };
      case 'canceled':
      case 'cancelled':
        return { text: 'בוטל', bg: '#FEE2E2', color: '#DC2626' };
      default:
        return { text: appointment?.status, bg: '#F3F4F6', color: '#6B7280' };
    }
  }, [appointment?.status]);

  // בדיקה מוקדמת אם המשתמש מחובר (רק עבור autofill offers)
  useEffect(() => {
    if (!isAutofillOffer) {
      setCheckingAuth(false);
      setIsUserAuthenticated(true);
      return;
    }

    // בדוק מיד אם יש customerPhone ב-localStorage
    const customerPhone = localStorage.getItem('customerPhone');

    // אם יש customerPhone - המשתמש מחובר, לא צריך מודאל
    if (customerPhone) {
      setIsUserAuthenticated(true);
      setShowAuthExplanation(false);
      setCheckingAuth(false);
      return;
    }

    // אם אין customerPhone - תבצע בדיקה מלאה
    const checkAuthentication = async () => {
      const customerId = await resolveCustomerIdFromLocalPhone();
      setIsUserAuthenticated(!!customerId);

      if (!customerId) {
        setShowAuthExplanation(true);
      }

      setCheckingAuth(false);
    };

    checkAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutofillOffer]);

  const handleCancel = async () => {
    try {
      setLoading(true);
      await onCancel(appointment.id);
      toast.success('התור בוטל');
      onClose();
    } catch (e: any) {
      toast.error(e.message || 'שגיאה בביטול התור');
    } finally {
      setLoading(false);
      setShowCancelConfirm(false);
    }
  };

  /**
   * ✅ מחזיר customerId או null
   * ❌ לא זורק שגיאות (כדי לא להציג טוסטים "שגיאה" כשפשוט לא מחובר)
   */
  const resolveCustomerIdFromLocalPhone = async (): Promise<string | null> => {
    const rawPhone = localStorage.getItem('customerPhone');
    const bId = localStorage.getItem('businessId');
    if (!rawPhone || !bId) return null;

    const phone = normalizePhone(rawPhone);

    let { data, error } = await supabase
      .from('customers')
      .select('id')
      .eq('business_id', bId)
      .eq('phone', phone)
      .maybeSingle();

    if (!data && !error) {
      const alt = phone.startsWith('+972') ? '0' + phone.slice(4) : phone;
      const r2 = await supabase
        .from('customers')
        .select('id')
        .eq('business_id', bId)
        .eq('phone', alt)
        .maybeSingle();

      data = r2.data as any;
    }

    return (data as any)?.id ?? null;
  };

  /**
   * מטפל בלחיצה על "התחבר כעת" במודאל ההסבר
   * פותח את OtpDialog לאימות מיידי
   */
  const handleOpenAuthFromExplanation = () => {
    setShowAuthExplanation(false);
    setShowOtpDialog(true);
  };

  /**
   * מטפל בהצלחת אימות OTP
   * שומר את הטלפון ב-localStorage ומפעיל את תהליך התפיסה
   */
  const handleOtpSuccess = async (phone: string) => {
    localStorage.setItem('customerPhone', phone);
    setIsUserAuthenticated(true);
    setShowOtpDialog(false);

    toast.success('התחברת בהצלחה!');

    await handleClaimAndCreateNewAppointment();
  };

  /**
   * ✅ Claim תור שהתפנה
   * אם אין התחברות → שומרים את ה-URL ומנווטים להתחברות.
   * אם הצליח → סוגרים מודאל ומעבירים ל-booking link נקי (בלי query params)
   *
   * ✅ תיקוני יציבות:
   * - guard נגד דאבל קליק (claimLoading)
   * - הודעת הצלחה שונה אם כבר היה תור (alreadyExisted)
   * - טיפול 409 ברור ("מישהו תפס לפני רגע")
   */
  const handleClaimAndCreateNewAppointment = async () => {
    // ✅ guard נגד דאבל-קליק / שתי בקשות במקביל
    if (claimLoading) return;

    try {
      setClaimLoading(true);

      const customerId = await resolveCustomerIdFromLocalPhone();

      // לא מחובר → שומרים את ה-URL ומנווטים לדף ההתחברות
      if (!customerId) {
        const currentUrl = window.location.href;
        sessionStorage.setItem('returnToAfterAuth', currentUrl);

        const bookingLink = getBookingLinkWithoutQueryParams();
        window.location.href = bookingLink;
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const url =
        `${supabaseUrl}/functions/v1/fill-open-slot` +
        `?mode=claim&appointmentId=${encodeURIComponent(appointment.id)}` +
        `&customerId=${encodeURIComponent(customerId)}`;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          apikey: anonKey,
        },
      });

      const data = await res.json().catch(() => null);

      // ✅ 409 = מישהו אחר תפס
      if (res.status === 409) {
        throw new Error('מישהו אחר תפס את התור לפני רגע');
      }

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || 'שגיאה בשריון התור');
      }

      // ✅ הודעה מדויקת לפי מצב
      if (data?.alreadyExisted) {
        toast.success('כבר שמרנו לך את התור 👍', { duration: 2000, icon: '✅' });
      } else {
        toast.success('התור נשמר לך!', { duration: 2000, icon: '✅' });
      }

      // ✅ הכי חשוב: לנקות את הפרמטרים כדי שלא יפתח שוב הצעה
      const cleanBookingUrl = getCleanBookingUrl();

      // סוגרים את מודאל פרטי התור
      onClose();

      // ✅ נועל כפתור עד מעבר (מונע לחיצות בזמן redirect)
      setClaimLoading(true);

      // ✅ redirect נקי (replace כדי שלא יחזור אחורה ל-autofill)
      window.location.replace(cleanBookingUrl);
    } catch (error: any) {
      toast.error(error?.message || 'שגיאה בשריון התור');
    } finally {
      // אם עברנו redirect, זה לא באמת ירוץ; אם נשארנו בעמוד — נחזיר מצב
      setClaimLoading(false);
    }
  };

  const showActions =
    isAutofillOffer || !['canceled', 'cancelled', 'completed', 'done'].includes(appointment.status);

  // מודאל הסבר להתחברות (רק עבור autofill offers כשהמשתמש לא מחובר)
  if (showAuthExplanation && !isUserAuthenticated) {
    return (
      <>
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-6"
            onClick={() => {
              setShowAuthExplanation(false);
              onClose();
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
            {/* אייקון מנעול מרכזי */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-8 text-center relative overflow-hidden">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
              >
                <Lock className="text-white" size={40} />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-2xl font-bold text-white"
              >
                נדרש אימות
              </motion.h2>
            </div>

            {/* תוכן ההסבר */}
            <div className="p-6 space-y-4">
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-gray-700 text-center text-base leading-relaxed"
              >
                כדי לאשר את התור צריך להתחבר ולאמת את מספר הטלפון שלך
              </motion.p>

              {/* פרטי התור */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-blue-50 rounded-2xl p-4 space-y-2"
              >
                <p className="text-sm text-gray-500 text-center mb-2">פרטי התור</p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                  <Calendar size={16} className="text-blue-500" />
                  <span>{format(parseISO(appointment.start_time), 'd MMMM', { locale: he })}</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                  <Clock size={16} className="text-blue-500" />
                  <span>{format(parseISO(appointment.start_time), 'HH:mm')}</span>
                </div>
                {appointment.users?.name && (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                    <User size={16} className="text-blue-500" />
                    <span>{appointment.users.name}</span>
                  </div>
                )}
              </motion.div>

              {/* כפתורים */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="space-y-3 pt-2"
              >
                <button
                  onClick={handleOpenAuthFromExplanation}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-2xl font-bold text-lg shadow-lg active:scale-[0.98] transition-all duration-200"
                  style={{ boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4)' }}
                >
                  התחבר כעת
                </button>
                <button
                  onClick={() => {
                    setShowAuthExplanation(false);
                    onClose();
                  }}
                  className="w-full bg-gray-100 text-gray-700 py-4 rounded-2xl font-semibold text-lg active:scale-[0.98] transition-all duration-200 hover:bg-gray-200"
                >
                  אולי מאוחר יותר
                </button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
        </AnimatePresence>

        {/* OTP Dialog */}
        <OtpDialog
          open={showOtpDialog}
          onClose={() => setShowOtpDialog(false)}
          onSuccess={handleOtpSuccess}
        />
      </>
    );
  }

  // Loading בזמן בדיקת אימות
  if (checkingAuth && isAutofillOffer) {
    return (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="w-12 h-12 border-4 border-white border-t-transparent rounded-full"
          />
        </motion.div>

        {/* OTP Dialog */}
        <OtpDialog
          open={showOtpDialog}
          onClose={() => setShowOtpDialog(false)}
          onSuccess={handleOtpSuccess}
        />
      </>
    );
  }

  return (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{ type: 'spring', damping: 35, stiffness: 400 }}
      className="fixed inset-0 bg-white z-50 flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header Bar - iOS Minimal */}
      <div className="relative pt-4 pb-3 border-b border-gray-100">
        <div className="absolute top-2 left-1/2 -translate-x-1/2">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>
        <div className="px-6 pt-4 flex justify-between items-center">
          <div className="w-16" />
          <h2 className="text-xl font-semibold text-gray-900">פרטי התור</h2>
          <button
            onClick={onClose}
            className="text-blue-500 font-medium text-base active:opacity-60 transition-opacity"
          >
            סגור
          </button>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {/* Status Badge - Minimal (Hide if canceled) */}
        {!['canceled', 'cancelled'].includes(appointment?.status) && (
          <div className="flex justify-center py-6">
            <motion.span
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="px-5 py-2 rounded-full text-sm font-medium"
              style={{
                background: statusConfig.bg,
                color: statusConfig.color,
              }}
            >
              {statusConfig.text}
            </motion.span>
          </div>
        )}

        {/* Details List - Clean & Simple */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="space-y-1 mb-6"
        >
          {/* Service Name */}
          {appointment.services?.name && (
            <div className="bg-gray-50 rounded-2xl p-4">
              <p className="text-xs text-gray-500 mb-1">שירות</p>
              <p className="text-base font-semibold text-gray-900">
                {appointment.services.name}
              </p>
            </div>
          )}

          {/* Date */}
          <div className="bg-gray-50 rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="text-gray-400" size={20} />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">תאריך</p>
                <p className="text-base font-semibold text-gray-900">
                  {format(parseISO(appointment.start_time), 'EEEE, d MMMM', { locale: he })}
                </p>
              </div>
            </div>
          </div>

          {/* Time */}
          <div className="bg-gray-50 rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="text-gray-400" size={20} />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">שעה</p>
                <p className="text-base font-semibold text-gray-900">
                  {format(parseISO(appointment.start_time), 'HH:mm')}
                </p>
              </div>
            </div>
          </div>

          {/* Staff Member */}
          <div className="bg-gray-50 rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <User className="text-gray-400" size={20} />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">מטפל/ת</p>
                <p className="text-base font-semibold text-gray-900">
                  {appointment.users?.name || 'ללא העדפה'}
                </p>
              </div>
            </div>
          </div>

          {/* Price */}
          {(appointment.services?.price || appointment.price) && (
            <div className="bg-gray-50 rounded-2xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-gray-400 text-lg font-bold">₪</div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">מחיר</p>
                  <p className="text-base font-semibold text-gray-900">
                    ₪{appointment.services?.price || appointment.price}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {appointment.notes && (
            <div className="bg-gray-50 rounded-2xl p-4">
              <p className="text-xs text-gray-500 mb-1">הערות</p>
              <p className="text-sm text-gray-700 leading-relaxed">
                {appointment.notes}
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Actions - Fixed at Bottom - Minimal iOS */}
      {showActions && (
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="p-6 pt-3 bg-white border-t border-gray-100 space-y-3"
        >
          {isAutofillOffer && (
            <button
              onClick={handleClaimAndCreateNewAppointment}
              disabled={claimLoading}
              className="w-full bg-blue-500 text-white py-3.5 rounded-xl font-medium text-base active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {claimLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                  />
                  שומר תור...
                </span>
              ) : (
                'אשר תור'
              )}
            </button>
          )}

          <button
            onClick={() => setShowCancelConfirm(true)}
            disabled={loading}
            className="w-full bg-gray-100 text-red-500 py-3.5 rounded-xl font-medium text-base active:opacity-70 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {loading ? 'מבטל תור...' : 'בטל תור'}
          </button>
        </motion.div>
      )}

      {/* Confirm Cancel - Minimal iOS Alert */}
      <AnimatePresence>
        {showCancelConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-6"
            onClick={() => setShowCancelConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 30, stiffness: 400 }}
              className="bg-white rounded-2xl w-full max-w-xs overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Alert Content */}
              <div className="p-6 text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">ביטול תור</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  האם אתה בטוח שברצונך לבטל את התור?
                </p>
              </div>

              {/* Alert Actions - iOS Style */}
              <div className="border-t border-gray-200">
                <button
                  onClick={handleCancel}
                  disabled={loading}
                  className="w-full py-3 text-red-500 font-medium text-base border-b border-gray-200 active:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'מבטל...' : 'בטל תור'}
                </button>
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={loading}
                  className="w-full py-3 text-blue-500 font-semibold text-base active:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  ביטול
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* OTP Dialog - מחוץ לכל המודאלים */}
      <OtpDialog
        open={showOtpDialog}
        onClose={() => setShowOtpDialog(false)}
        onSuccess={handleOtpSuccess}
      />
    </motion.div>
  );
}
